import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Save,
  ImagePlus,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import DbTableCatalogue, { tablePurpose } from '../../components/DbTableCatalogue';
import { useUiLocale } from '../../i18n/useUiLocale';
import { resolveSettingsNav } from '../../navigation/settingsNavigation';

const DEFAULT_COMPANY = 'company-001';

function Settings() {
  const { user } = useAuthStore();
  const logoInputRef = useRef(null);
  const companyId = user?.company_id || user?.companyId || DEFAULT_COMPANY;

  const [loading, setLoading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    address: '',
    phone: '',
    email: '',
    contact_person: '',
    license_number: '',
    website: '',
  });
  const [hasLogo, setHasLogo] = useState(false);

  const canDocs =
    user?.role === 'ADMIN' || user?.role === 'MANAGER' || user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER';
  const canCycle = canDocs;
  // MASTER has full office authority — company profile must be editable
  const canCompany =
    user?.role === 'ADMIN' ||
    user?.role === 'MANAGER' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'MASTER';
  const canDb =
    user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER';

  const locale = useUiLocale();
  const [dbTables, setDbTables] = useState([]);
  const [dbTablesLoading, setDbTablesLoading] = useState(false);
  const [dbTableFilter, setDbTableFilter] = useState('');

  useEffect(() => {
    if (!canDb) return;
    setDbTablesLoading(true);
    api
      .get('/settings/db/tables')
      .then((res) => setDbTables(res.data.data || []))
      .catch(() => setDbTables([]))
      .finally(() => setDbTablesLoading(false));
  }, [canDb]);

  const filteredDbTables = dbTables.filter((t) => {
    const q = dbTableFilter.trim().toLowerCase();
    if (!q) return true;
    const purpose = tablePurpose(t, locale) || '';
    return (
      String(t.name || '')
        .toLowerCase()
        .includes(q) ||
      purpose.toLowerCase().includes(q) ||
      String(t.category || '')
        .toLowerCase()
        .includes(q)
    );
  });

  const releaseLogoPreview = () => {
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoPreviewUrl('');
  };

  const loadLogoPreview = async () => {
    releaseLogoPreview();
    try {
      const res = await api.get(`/companies/${companyId}/logo`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setLogoPreviewUrl(url);
      setHasLogo(true);
    } catch {
      setHasLogo(false);
    }
  };

  const runAutoCycle = async () => {
    try {
      setLoading(true);
      const res = await api.post('/inspection-notify/run-daily', {});
      const p = res.data?.prepare || {};
      const f = res.data?.followUp || {};
      toast.success(
        `Cycle ran: ${p.created || 0} notified, ${p.emailed || 0} emailed, ${f.visitStarted || 0} in progress`
      );
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Cycle failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanyInfo();
    return () => releaseLogoPreview();
  }, [companyId]);

  const loadCompanyInfo = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/profile`);
      const data = response.data?.data || response.data;
      if (data) {
        setCompanyInfo({
          company_name: data.name || '',
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          contact_person: data.contact_person || '',
          license_number: data.license_number || '',
          website: data.website || '',
        });
        setHasLogo(!!data.has_logo);
        if (data.has_logo) await loadLogoPreview();
        else releaseLogoPreview();
      }
    } catch (error) {
      console.error('Failed to load company info:', error);
      // Do not fall back to stale localStorage — other devices may have newer server data
      toast.error(error.response?.data?.error || 'Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await api.put(`/companies/${companyId}/profile`, {
        name: companyInfo.company_name,
        address: companyInfo.address,
        phone: companyInfo.phone,
        email: companyInfo.email,
        contact_person: companyInfo.contact_person,
        license_number: companyInfo.license_number,
        website: companyInfo.website,
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setLoading(true);
      const form = new FormData();
      form.append('logo', file);
      const res = await api.post(`/companies/${companyId}/logo`, form);
      const data = res.data?.data;
      setHasLogo(!!data?.has_logo);
      await loadLogoPreview();
      toast.success('Company logo updated');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Logo upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Remove company logo?')) return;
    try {
      setLoading(true);
      await api.delete(`/companies/${companyId}/logo`);
      releaseLogoPreview();
      setHasLogo(false);
      toast.success('Logo removed');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to remove logo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveSettingsNav}
        title="Settings"
        infoTitle="Settings"
        infoChildren={
          <p>
            Company details, logo, and preferences. Use the header sub-menu for checklist templates and database tools.
            Run auto cycle stays in the header actions.
          </p>
        }
        actions={
          <>
            {canCycle ? (
              <button
                type="button"
                disabled={loading}
                onClick={runAutoCycle}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-50"
              >
                Run auto cycle now
              </button>
            ) : null}
          </>
        }
      />

      {/* Company Information */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Building2 className="w-5 h-5 mr-2" />
            Company Information
          </h2>
        </div>

        {canCompany ? (
          <div className="mb-8 flex flex-wrap items-start gap-6 p-4 rounded-lg border border-gray-200 bg-slate-50">
            <div
              className="w-28 h-28 rounded-lg border border-gray-200 bg-card flex items-center justify-center overflow-hidden shrink-0"
            >
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Company logo"
                  className="max-w-full max-h-full object-contain p-2"
                />
              ) : (
                <Building2 className="w-10 h-10 text-gray-300" />
              )}
            </div>
            <div className="min-w-0 space-y-2">
              <p className="text-sm font-medium text-gray-900">Company logo</p>
              <p className="text-xs text-gray-500 max-w-md">
                PNG, JPEG, WebP, or GIF ??max 2 MB. Used on paperwork and admin branding.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => logoInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  <ImagePlus className="w-4 h-4" />
                  {hasLogo ? 'Change logo' : 'Upload logo'}
                </button>
                {hasLogo ? (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleLogoRemove}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                ) : null}
              </div>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={handleLogoPick}
              />
            </div>
          </div>
        ) : null}

        <div className="space-y-6">
          <p className="text-sm text-slate-600 -mt-2">
            Logo appears on the left of Form 12A; address, website and email sit beside it in compact 9pt
            text. Leave a field blank to omit it from the letterhead. Form 12A also requires each
            building&apos;s street address, legal description, compliance schedule no., and issue date —
            photos belong only on the companion Inspection report, never on Form 12A.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name *
              </label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                required
                value={companyInfo.company_name}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Enter company name"
              />
            </div>

            <div>
              <label htmlFor="license_number" className="block text-sm font-medium text-gray-700 mb-2">
                License Number
              </label>
              <input
                type="text"
                id="license_number"
                name="license_number"
                value={companyInfo.license_number}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Enter license number"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                required
                value={companyInfo.address}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Unit 6, 7-9 Marken Place Wairau Valley Auckland 0627"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={companyInfo.phone}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Enter phone number"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={companyInfo.email}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="admin@cbsl.co.nz"
              />
            </div>

            <div>
              <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-2" />
                Contact Person
              </label>
              <input
                type="text"
                id="contact_person"
                name="contact_person"
                value={companyInfo.contact_person}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="Enter contact person name"
              />
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={companyInfo.website}
                onChange={handleChange}
                disabled={!canCompany}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50"
                placeholder="www.cbsl.co.nz"
              />
            </div>
          </div>
        </div>

        {canCompany ? (
          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        ) : null}
      </div>

      {/* User Information */}
      <div className="bg-card rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <User className="w-5 h-5 mr-2" />
          Current User
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Name</p>
            <p className="text-gray-900">{user?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Role</p>
            <p className="text-gray-900">{user?.role || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Email</p>
            <p className="text-gray-900">{user?.email || 'N/A'}</p>
          </div>
        </div>
      </div>

      {canDb ? (
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {locale === 'ko' ? '데이터베이스 테이블' : 'Database tables'}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {locale === 'ko'
                  ? '실제 MySQL 테이블 이름과 기본 설명입니다. 데이터/구조 편집은 DB Setting에서 잠금 해제 후 가능합니다.'
                  : 'Live MySQL table names with a short purpose. Unlock under DB Setting to edit data or structure.'}
              </p>
            </div>
            <Link
              to="/settings/db"
              className="text-sm font-semibold text-primary-700 hover:underline"
            >
              {locale === 'ko' ? 'DB Setting 열기 →' : 'Open DB Setting →'}
            </Link>
          </div>
          <DbTableCatalogue
            tables={filteredDbTables}
            loading={dbTablesLoading}
            filter={dbTableFilter}
            onFilterChange={setDbTableFilter}
            unlocked={false}
          />
        </div>
      ) : null}
    </div>
  );
}

export default Settings;
